import { createClient } from '@sanity/client'
import imageUrlBuilder from '@sanity/image-url'

// Localhost guard — when running `npm run web` on a developer's machine,
// force the development dataset regardless of how the bundle was built
// or what env vars happen to be set. Without this, a stray
// NODE_ENV=production (or a stale `studio/.env.production`-derived
// bundle) would point the data client AND the Sanity edit-link helper
// at the production Studio, where doc IDs may differ from the dev
// dataset and edit intent URLs fail to resolve.
function isLocalhost() {
    if (typeof window === 'undefined') return false;
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}

function effectiveDataset() {
    if (isLocalhost()) return 'development';
    return process.env.SANITY_STUDIO_DATASET || 'development';
}

export const client = createClient({
    projectId: process.env.SANITY_STUDIO_PROJECT_ID || '7zjqa9p9',
    dataset: effectiveDataset(),
    useCdn: false, // set to `false` to bypass the edge cache
    apiVersion: '2023-05-03', // use current date (YYYY-MM-DD) to target the latest API version
})

const builder = imageUrlBuilder(client)

export function urlFor(source) {
    return builder.image(source)
}

// Intercept ?edit parameter and store/remove cookie
function checkEditMode() {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const hasEditParam = urlParams.has('edit');

    if (hasEditParam) {
        // Set/refresh cookie if parameter is present
        document.cookie = "sanityEditMode=true; path=/; max-age=86400"; // 1 day
    }

    // Show/hide navbar toggle based on edit mode
    initNavbarEditToggle();
}

// Append ?edit=true to internal links if edit mode is active
function initEditNavigation() {
    if (typeof document === 'undefined') return;

    document.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (!link || !link.href) return;

        // Check if cookie exists
        const hasCookie = document.cookie.split(';').some((item) => item.trim().startsWith('sanityEditMode=true'));
        if (!hasCookie) return;

        try {
            const url = new URL(link.href, window.location.origin);
            const isInternal = url.origin === window.location.origin;

            if (isInternal) {
                // Manually append 'edit' parameter if not present
                const urlObj = new URL(link.href, window.location.origin);
                if (!urlObj.search.includes('edit')) {
                    const separator = urlObj.search ? '&' : '?';
                    link.href = link.href + separator + 'edit';
                }
            }
        } catch (err) {
            // Skip if invalid URL
        }
    });
}

// Handle Navbar Edit Toggle
function initNavbarEditToggle() {
    if (typeof document === 'undefined') return;

    const toggleContainer = document.getElementById('navbarEditToggleContainer');
    let toggleBtn = document.getElementById('navbarEditToggle');

    if (!toggleContainer || !toggleBtn) return;

    const hasCookie = document.cookie.split(';').some((item) => item.trim().startsWith('sanityEditMode=true'));
    const urlParams = new URLSearchParams(window.location.search);
    const hasEditParam = urlParams.has('edit');

    // 1. Show toggle and check the box ONLY if edit mode is active
    if (hasEditParam || hasCookie) {
        toggleContainer.style.setProperty('display', 'flex', 'important');
        toggleBtn.checked = true; // Handled by JS now, not HTML
    } else {
        toggleContainer.style.setProperty('display', 'none', 'important');
        return;
    }

    // 2. Prevent the "Double Click" bug by clearing old duplicate listeners
    const newBtn = toggleBtn.cloneNode(true);
    toggleBtn.parentNode.replaceChild(newBtn, toggleBtn);
    toggleBtn = newBtn; // Update reference

    // 3. Toggle off change handler
    toggleBtn.addEventListener('change', () => {
        if (!toggleBtn.checked) {
            // A. Instantly rip it out of the UI
            toggleContainer.style.setProperty('display', 'none', 'important');

            // B. Brutally destroy the cookie (covers both local and production domain variations)
            document.cookie = "sanityEditMode=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
            document.cookie = "sanityEditMode=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname + ";";

            // C. Clean the URL and execute a hard reload
            const url = new URL(window.location.href);
            url.searchParams.delete('edit');
            window.location.replace(url.toString()); // .replace prevents the back-button loop
        }
    });
}

// Run on init
if (typeof window !== 'undefined') {
    checkEditMode();
    initEditNavigation();
}

export function isEditModeActive() {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    const hasCookie = document.cookie.split(';').some((item) => item.trim().startsWith('sanityEditMode=true'));
    return urlParams.has('edit') || hasCookie;
}

export function resolveInternalLink(href) {
    if (!href) return href;

    let finalHref = href;

    // 1. Clean up legacy localhost or production origins
    try {
        const urlObj = new URL(href, window.location.origin);
        if (urlObj.origin === window.location.origin || urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1') {
            finalHref = urlObj.pathname + urlObj.search + urlObj.hash;
        }
    } catch (e) {
        // Not a full URL, continue with relative path
    }

    // 2. Map legacy /services/ to /how-we-work/
    if (finalHref.includes('/services/')) {
        finalHref = finalHref.replace('/services/', '/how-we-work/');
    }

    // 3. Ensure trailing slash for directory-based routing (if not a file)
    if (!finalHref.includes('.') && !finalHref.endsWith('/') && !finalHref.includes('?') && !finalHref.includes('#') && finalHref !== '#') {
        finalHref += '/';
    }

    // 4. Handle Edit Mode parameter
    if (isEditModeActive()) {
        try {
            const separator = finalHref.includes('?') ? '&' : '?';
            if (!finalHref.includes('edit')) {
                // If it has a hash, inject before the hash
                if (finalHref.includes('#')) {
                    const parts = finalHref.split('#');
                    return parts[0] + separator + 'edit#' + parts[1];
                }
                return finalHref + separator + 'edit';
            }
        } catch (e) {
            // Fallback
        }
    }

    return finalHref;
}

export function ensureSanityEditStyles() {
    if (typeof document === 'undefined') return;
    if (document.getElementById('sanity-edit-styles')) return;
    const style = document.createElement('style');
    style.id = 'sanity-edit-styles';
    style.textContent = `
            .sanity-edit-btn {
                position: absolute;
                top: 1rem;
                right: 1rem;
                padding: 6px 8px;
                background-color: #eab308;
                color: #000;
                border-radius: 9999px;
                box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                transition: all 0.3s ease-out;
                cursor: pointer;
                display: flex;
                align-items: center;
                z-index: 1050;
                text-decoration: none !important;
                border: none;
                width:auto;
            }
            .sanity-edit-btn:hover {
                background-image: linear-gradient(to right, #facc15, #f59e0b, #86efac);
                padding-right: 1rem;
                color: #000;
            }
            .sanity-edit-btn svg {
                transition: transform 0.3s;
                flex-shrink: 0;
            }
            .sanity-edit-btn:hover svg {
                transform: rotate(12deg);
            }
            .sanity-edit-btn span {
                max-width: 0;
                overflow: hidden;
                white-space: nowrap;
                transition: all 0.3s ease-out;
                opacity: 0;
                font-size: 0.875rem;
                font-weight: 500;
                margin-left: 0;
            }
            .sanity-edit-btn:hover span {
                max-width: 200px;
                margin-left: 0.5rem;
                opacity: 1;
            }
        `;
    document.head.appendChild(style);
}

// Pick the right Studio URL for the current dataset so edit links never
// cross-link a dev/staging build to the production studio (or vice versa).
// Derived purely from the effective dataset (see `effectiveDataset()`
// above) so the data client and edit-link target can never disagree —
// SANITY_STUDIO_URL is intentionally NOT consulted as a separate signal,
// because a stale prod URL paired with dev data is exactly the
// "edit link points at the wrong project" bug we're avoiding.
export function resolveStudioUrl() {
    const dataset = effectiveDataset();
    return dataset === 'production'
        ? 'https://coriolis.sanity.studio'
        : 'https://coriolis-staging.sanity.studio';
}

export function getSanityEditLink(docId, type, path, customLabel) {
    // Only return the link if the cookie is set or if we forcibly bypass it (for development)
    if (!isEditModeActive() || !docId || !type) return '';

    const studioUrl = resolveStudioUrl();
    const href = `${studioUrl}/intent/edit/id=${docId};type=${type};path=${path}`;

    ensureSanityEditStyles();

    const label = customLabel || ("Edit " + path.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) { return str.toUpperCase(); }) + " section");

    return `
        <a href="${href}" title="${label}" aria-label="${label}" target="_blank" class="sanity-edit-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 21h8"></path><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path></svg>
            <span>${label}</span>
        </a>
    `;
}
